// IND-018: textos de la propuesta del libro de recuento («Hoja STP-MP-<MES> →
// recuento del dd/mm/aaaa», desglose por tabla con su cabecera y cuántos
// materiales tienen ACTUAL distinto de MES ANTERIOR). Solo presentación.
import type {
  RecountWorkbookFamily,
  RecountWorkbookSheet,
  RecountWorkbookTable,
} from '../services/industrialCostService';
import { formatDataDay } from './dailyLoadRunFile';
import { formatEsNumber } from './numberFormat';

/** Día 1 del mes siguiente (el ACTUAL de un mes es el recuento de ese día). */
export function countDateFor(month: number, year: number): string {
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  return `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`;
}

export function sheetOptionLabel(sheet: RecountWorkbookSheet): string {
  const day = formatDataDay(sheet.count_date);
  return (
    `Hoja ${sheet.sheet_name} → ` +
    (day ? `recuento del ${day}` : 'sin año') +
    (sheet.year_source === 'filename' ? ' (año tomado del nombre del fichero)' : '') +
    (!sheet.year_missing && !sheet.actual_filled ? ' (sin contar)' : '')
  );
}

const TABLE_LABELS: Record<RecountWorkbookTable['kind'], string> = {
  raw_materials: 'materias primas',
  packaging: 'embalajes',
  single: 'de la tabla',
  other: 'de otra tabla',
};

/** «101 materias primas (CODIGO / ACTUAL) · 68 embalajes (REFERENCIA / ACTUAL)
 * · 1 de la tabla REFERENCIA / CANTIDAD». Las tablas del mismo tipo se suman. */
export function tablesBreakdownText(tables: RecountWorkbookTable[] | undefined): string {
  const byKind = new Map<RecountWorkbookTable['kind'], { count: number; header: string }>();
  for (const table of tables ?? []) {
    const header = `${table.code_header} / ${table.quantity_header ?? '—'}`;
    const current = byKind.get(table.kind);
    byKind.set(table.kind, {
      count: (current?.count ?? 0) + table.materials_count,
      header: current?.header ?? header,
    });
  }
  return (['raw_materials', 'packaging', 'single', 'other'] as const)
    .filter((kind) => byKind.has(kind))
    .map((kind) => {
      const item = byKind.get(kind)!;
      return kind === 'single' || kind === 'other'
        ? `${formatEsNumber(item.count)} ${TABLE_LABELS[kind]} ${item.header}`
        : `${formatEsNumber(item.count)} ${TABLE_LABELS[kind]} (${item.header})`;
    })
    .join(' · ');
}

/** «101 materias primas (95 CODIGO / ACTUAL + 6 REFERENCIA / CANTIDAD) · 68 embalajes
 * (REFERENCIA / ACTUAL) · 1 más (STP-900031)»: cada código una vez, con las
 * tablas de las que sale. */
export function familiesBreakdownText(families: RecountWorkbookFamily[] | undefined): string {
  return (families ?? [])
    .filter((family) => family.materials_count > 0)
    .map((family) => {
      const count = formatEsNumber(family.materials_count);
      if (family.kind === 'other') {
        return `${count} más${family.sample_codes.length > 0 ? ` (${family.sample_codes.join(', ')})` : ''}`;
      }
      const label = family.kind === 'raw_materials' ? 'materias primas' : 'embalajes';
      // «95 CODIGO / ACTUAL + 6 REFERENCIA / CANTIDAD» si sale de varias tablas.
      const origin =
        family.by_table && family.by_table.length > 1
          ? family.by_table
              .map((table) => `${formatEsNumber(table.materials_count)} ${table.header}`)
              .join(' + ')
          : family.headers.join(' · ');
      return `${count} ${label} (${origin})`;
    })
    .join(' · ');
}

/** «ACTUAL distinto en 160 de 169» (para ver si el recuento está completo). */
export function changedText(sheet: RecountWorkbookSheet): string | null {
  if (sheet.actual_changed_count == null || !sheet.compared_materials) return null;
  return `ACTUAL distinto en ${formatEsNumber(sheet.actual_changed_count)} de ${formatEsNumber(sheet.compared_materials)}`;
}

/** Desglose guardado de una carga: «101 materias primas · 68 embalajes · 1 más». */
export function loadBreakdownText(
  breakdown:
    | { raw_materials?: number | null; packaging?: number | null; other?: number | null }
    | null
    | undefined,
): string | null {
  if (!breakdown) return null;
  return (
    `${formatEsNumber(breakdown.raw_materials ?? 0)} materias primas · ` +
    `${formatEsNumber(breakdown.packaging ?? 0)} embalajes` +
    ((breakdown.other ?? 0) > 0 ? ` · ${formatEsNumber(breakdown.other ?? 0)} más` : '')
  );
}
